import { Alert, Modal, Switch, Tooltip, Typography, message } from "antd";
import axios from "axios";
import { FEATURE_FLAGS_QUERY_KEY } from "common/query-keys";
import { useGetAdminSession } from "hooks/useGetUserSession";
import { useRequestProcessor } from "hooks/useRequestProcessor";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FEATURE_FLAG_PERMISSION_WHITELIST } from "./FeatureFlags";

const { Text } = Typography;

const FeatureFlagToggle = ({
    status,
    id,
    defaultStatus,
    requiresDotsApp = false,
    hasDotsApp = false,
    dotsAppUnknown = false,
}) => {
    const { userId } = useParams();

    const [isLoading, setIsLoading] = useState(false);
    const [checked, setIsChecked] = useState(status);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const userSession = useGetAdminSession();

    const { useMutate } = useRequestProcessor();

    const { mutateAsync } = useMutate(
        [FEATURE_FLAGS_QUERY_KEY, userId],
        (data) => axios.post("admin/user/flag/update", data),
        {
            enabled: !!userId,
        },
    );

    // Persists the change. Shared by the plain path and by the warning modal's Proceed,
    // so proceeding saves the flag exactly as an unguarded toggle would — the warning
    // informs the admin, it never blocks the update.
    const commitToggleStatus = (nextStatus) => {
        setIsLoading(true);
        setIsChecked(nextStatus);

        return mutateAsync({
            brandUserId: userId,
            featureFlagId: id,
            status: nextStatus,
        })
            .then((res) => {
                if (res.data.success) {
                    message.success("Feature flag status updated successfully");
                }
            })
            .catch(() => {
                message.error("Failed to update feature flag status");
                setIsChecked(!nextStatus);
            })
            .finally(() => setIsLoading(false));
    };

    const confirmEnableWithoutDotsApp = () => {
        Modal.confirm({
            centered: true,
            icon: null,
            className: "feature-flag-confirm-modal",
            content: (
                <div style={{ width: "100%" }}>
                    <Alert
                        type="warning"
                        message={
                            <div>
                                This brand has no{" "}
                                <Text strong underline>
                                    Dots App ID
                                </Text>{" "}
                                connected, so no payouts can be processed
                            </div>
                        }></Alert>
                    <p style={{ margin: "12px 0 0" }}>
                        Enabling this flag shows the Payments section in the
                        Brand Portal and lets the brand set up a payout
                        schedule, but every payout will fail until a Dots App ID
                        is set on the <Text strong>Automated Payouts</Text> tab.
                    </p>
                </div>
            ),
            okText: "Proceed",
            cancelText: "Cancel",
            onOk: () => commitToggleStatus(true),
        });
    };

    const handleToggleStatus = (nextStatus) => {
        // Only the enable direction is guarded, and only while the prerequisite is
        // missing. Turning the flag off is always safe.
        if (nextStatus && requiresDotsApp && !hasDotsApp) {
            confirmEnableWithoutDotsApp();
            return;
        }

        commitToggleStatus(nextStatus);
    };

    // The switch is disabled either because the flag is forced on globally, or because we
    // can't yet tell whether the brand has a Dots app. Explain whichever applies.
    const disabledReason = defaultStatus
        ? "This feature flag is enabled globally. It cannot be changed."
        : requiresDotsApp && dotsAppUnknown
        ? "Checking whether this brand has a connected Dots app. Reload the page if this doesn't clear."
        : null;

    const handleToggleTooltip = (isOpen) => {
        if ((isOpen && disabledReason) || !isOpen) {
            setIsTooltipOpen(isOpen);
        }
    };

    useEffect(() => {
        setIsChecked(status);
    }, [status]);

    const canEdit = true; // Override permissions check
    // FEATURE_FLAG_PERMISSION_WHITELIST?.length > 0
    //     ? FEATURE_FLAG_PERMISSION_WHITELIST.includes(
    //           userSession?.sessionUserId,
    //       )
    //     : true;

    return (
        <Tooltip
            open={isTooltipOpen}
            onOpenChange={handleToggleTooltip}
            title={disabledReason}>
            <Switch
                onChange={handleToggleStatus}
                checked={checked}
                loading={isLoading}
                disabled={!!disabledReason || !canEdit}
            />
        </Tooltip>
    );
};

export default FeatureFlagToggle;
